import type { Agent } from '@studio/models/agent';
import { Protocol as ControlProtocol } from '@studio/models/control';
import { Protocol as VideoProtocol } from '@studio/models/video';
import type { Room } from '@studio/models/room';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { Preview } from './types.js';
import * as Commands from '#~/device/commands/index.js';
import { isControlDiscovery, isVideoDiscovery } from '#~/device/discovery/index.js';
import type { Discovery } from '#~/device/discovery/index.js';
import type { Device$Video, Minimist } from '#~/device/index.js';
import { toDevice } from '#~/device/index.js';
import { logger } from '#~/logger/index.js';
import { extractMachineIDAndTypeFromPersistentID } from '#~/mediadevices/discovery.js';

export const urxCache: Record<string, boolean> = {};

/**
 * Wrapper around the standard `create` device command that sets the appropriate device defaults.
 *
 * @param roomId - Room ID.
 * @param agentId - Agent ID.
 * @param candidate - Candidate device.
 * @param token - The token.
 */
export const addDevice = async (
  roomId: Room['id'],
  agentId: Agent['id'],
  candidate: Minimist,
  token: string,
  urx?: T_Maybe<string>,
  options?: Commands.AddOptions
): Promise<Commands.AddResult> => {
  if (urx) {
    urxCache[urx] = true;
  }

  try {
    const result = await Commands.add(roomId, agentId, toDevice(candidate), token, options);
    // Cancellation before mutation leaves no preview allocation to reserve this source.
    if (urx && result?.kind === 'superseded') delete urxCache[urx];
    return result;
  } catch (error) {
    if (urx) {
      delete urxCache[urx];
    }

    throw error;
  }
};

/**
 * Protocols that reach a physical network camera at its IP. One camera can
 * answer several of these at once (e.g. NDI and RTSP/ONVIF), so the IP is what
 * identifies the camera across them. Local/USB capture is excluded: its host,
 * if any, is the bridge machine, not a camera.
 */
const NETWORK_CAMERA_PROTOCOLS: ReadonlySet<string> = new Set([
  VideoProtocol.RTSP,
  VideoProtocol.RTSPT,
  VideoProtocol.RTSPLT,
  VideoProtocol.NDI,
  VideoProtocol.IRIS,
  ControlProtocol.ONVIF
]);

/**
 * Could the device and the discovery sit behind the same bridge? An IP is only
 * an identity on one LAN: two bridges on separate networks can each see a
 * different camera at the same private IP. Only a known, disjoint bridge pair
 * rules a match out; when either side has no bridge recorded, the IP alone
 * decides. Same bridge-set notion as `cameraKey` in discovery-transports.ts.
 */
const isSameBridge = (device: Device$Video, discovery: Discovery): boolean => {
  const bridges = [discovery.agentId, ...(discovery.agentIds ?? [])].filter(Boolean);

  if (!device.agentId || bridges.length === 0) return true;

  return bridges.includes(device.agentId);
};

/** @internal */
export const isDiscoveryInUse = (device: Device$Video, discovery: Discovery): boolean => {
  const a = device.video.config;
  const b = (() => {
    if (isControlDiscovery(discovery)) return discovery.control;
    if (isVideoDiscovery(discovery)) return discovery.video;

    return discovery.audio;
  })();

  if (!b) return false;

  const hostMatches = 'host' in a && 'host' in b && a.host === b.host;
  const portMatches = 'port' in a && 'port' in b && a.port === b.port;

  const isNDI = a.protocol === 'ndi' && b.protocol === 'ndi';
  const isRTSP =
    a.protocol.startsWith('rtsp') && (b.protocol === a.protocol || b.protocol === 'onvif');

  if (isNDI) {
    return hostMatches && portMatches;
  }

  if (isRTSP) {
    return hostMatches;
  }

  // A camera added on one protocol must not come back as a new source on
  // another: once fingerprinting labels its RTSP/ONVIF stream, a camera added
  // over NDI would otherwise show in Discovery as a separate camera. Match on IP
  // + bridge across protocols. Same-protocol NDI keeps host+port above, since
  // one machine can send several NDI sources.
  const isCrossProtocolCamera =
    a.protocol !== b.protocol &&
    NETWORK_CAMERA_PROTOCOLS.has(a.protocol) &&
    NETWORK_CAMERA_PROTOCOLS.has(b.protocol);

  if (isCrossProtocolCamera) {
    // An empty host carries no identity; never let two blanks "match".
    return hostMatches && 'host' in a && !!a.host && isSameBridge(device, discovery);
  }

  if (
    'path' in a &&
    'path' in b &&
    'protocol' in a &&
    'protocol' in b &&
    a.protocol === b.protocol &&
    a.protocol === 'browsermedia'
  ) {
    const {
      machinePersistentID: machinePersistentIDA,
      label: labelA,
      offset: offsetA
    } = extractMachineIDAndTypeFromPersistentID(a.path as string);
    const {
      machinePersistentID: machinePersistentIDB,
      label: labelB,
      offset: offsetB
    } = extractMachineIDAndTypeFromPersistentID(b.path as string);

    const pathMatches =
      !!machinePersistentIDA &&
      machinePersistentIDA === machinePersistentIDB &&
      labelA === labelB &&
      offsetA === offsetB;

    return hostMatches || pathMatches;
  }

  const protocolMatches = a.protocol === b.protocol;
  return hostMatches && protocolMatches;
};

/** @internal */
export const removePreview = async (preview: T_Maybe<Preview>, token: string): Promise<void> => {
  const deviceId = preview?.deviceId;

  if (!deviceId) {
    return undefined;
  }

  logger.debug(`Removing preview device: ${deviceId}`);

  await Commands.remove(deviceId, token);

  // Keep failed removal discoverable so a later cleanup can try the same device.
  delete urxCache[preview.urx];
};

export const clearURXCache = (): void => {
  Object.keys(urxCache).forEach((µ) => {
    delete urxCache[µ];
  });
};
